import * as React from "react";
import { useWatch } from "react-hook-form";

import { type TriggerTemplateCatalog } from "@/shared/domain/triggers";

import { type AgentFormValues } from "./agentFormSchema";
import { agentVariableCatalog } from "./agentVariableCatalog";

const WATCHED = [
  "subject",
  "kickoffMessage",
  "goals",
  "variableMeta",
  "passthroughGoals",
  "passthroughVariables",
  "passthroughClientContext",
] as const;

/**
 * The `{{ }}` vocabulary the form currently offers, recomputed as the text
 * that declares it is typed.
 *
 * Only the fields that decide which variables exist are watched, so renaming
 * the agent does not rebuild the catalog. The subject is one of them: a
 * goal-less agent on a dataset or a session still declares that record's
 * variable.
 */
export function useAgentVariableCatalog(): TriggerTemplateCatalog {
  const [
    subject,
    kickoffMessage,
    goals,
    variableMeta,
    passthroughGoals,
    passthroughVariables,
    passthroughClientContext,
  ] = useWatch<AgentFormValues, typeof WATCHED>({ name: WATCHED });

  return React.useMemo(
    () =>
      agentVariableCatalog({
        name: "",
        description: "",
        subject,
        modelProfile: null,
        kickoffMessage,
        goals,
        skills: [],
        variableMeta,
        passthroughGoals,
        passthroughVariables,
        passthroughClientContext,
      }),
    [
      subject,
      kickoffMessage,
      goals,
      variableMeta,
      passthroughGoals,
      passthroughVariables,
      passthroughClientContext,
    ],
  );
}
